/*********************************************************************
 * © Copyright IBM Corp. 2024
 *********************************************************************/
import {useServices} from '../useServices'
import React, {useEffect, useState} from 'react'
import {DiffViewer} from '../DiffView'
import {MessageRowProps} from '../../columns/Message'
import {Loader} from '../../Loader'
import style from '../../style/index.css'
import intl from '../../utils/intl/intl'

export const DetailedViewDiffViewer = ({item}: {item: MessageRowProps['item']}) => {
  const {payload} = item
  const {layoutManagementClient} = useServices()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [beforeUpdate, setBeforeUpdate] = useState<string | null>(null)
  const [afterUpdate, setAfterUpdate] = useState<string | null>(null)
  const shouldShowMonacoEditor =
    item.area === 'detailedView' && item.eventType === 'detailedViewUpdated'

  useEffect(() => {
    async function retrieveDetailedViewVersions() {
      if (
        layoutManagementClient &&
        payload &&
        payload.versionBeforeUpdate &&
        payload.versionAfterUpdate
      ) {
        if (!payload.layoutId) {
          setIsLoading(false)
          return
        }

        const [before, after] = await Promise.all([
          layoutManagementClient?.getLayoutVersionById(
            payload.layoutId,
            payload.versionBeforeUpdate
          ),
          layoutManagementClient?.getLayoutVersionById(payload.layoutId, payload.versionAfterUpdate)
        ])
        setBeforeUpdate(JSON.stringify(before?.schema, null, '\t'))
        setAfterUpdate(JSON.stringify(after?.schema, null, '\t'))
        setIsLoading(false)
      }
    }

    if (shouldShowMonacoEditor) {
      retrieveDetailedViewVersions()
    }
  }, [shouldShowMonacoEditor])

  if (!shouldShowMonacoEditor) {
    return null
  }

  if (isLoading) {
    return <Loader />
  }

  return beforeUpdate && afterUpdate ? (
    <DiffViewer before={beforeUpdate} after={afterUpdate} language="json" options={{}} />
  ) : (
    <div className={style['detailed-view-no-diff-container']}>
      <div className={style['detailed-view-no-diff-message']}>
        {intl.formatMessage('Diff is not available')}
      </div>
    </div>
  )
}
